import React from "react";
import { Cloud, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

import { ThemeToggle } from "@/components/ThemeToggle";
import { Card } from "@/components/ui/card";
import { LanguageToggle } from "./LanguageToggle";

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
}

export function AuthLayout({ children, title, subtitle }: AuthLayoutProps) {
  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-background p-4">
      <div className="pointer-events-none absolute inset-0 bg-gradient-mesh opacity-70" />
      <div className="pointer-events-none absolute -left-[10%] -top-[10%] h-[40%] w-[40%] rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-[10%] -right-[10%] h-[40%] w-[40%] rounded-full bg-chart-2/10 blur-3xl" />

      <div className="absolute right-6 top-6 z-50 flex items-center gap-2">
        <LanguageToggle />
        <ThemeToggle className="static" />
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="group mb-4 flex h-16 w-16 cursor-pointer items-center justify-center overflow-hidden rounded-2xl bg-primary shadow-lg shadow-primary/20">
            <Cloud className="h-8 w-8 text-primary-foreground transition-transform group-hover:scale-110" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{title}</h1>
          <p className="mt-2 max-w-[300px] text-muted-foreground">{subtitle}</p>
        </div>

        <Card className="glass border-white/40 p-6 dark:border-white/5 md:p-8">{children}</Card>

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4" />
          <span>Cloudface Security Baseline v2.4</span>
        </div>
      </motion.div>
    </div>
  );
}
